import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Form, Row, Col, Dropdown } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import styles from './ActivityModal.module.css';
import { ConfirmationModalContext } from '../../../context/ConfirmationModalContext/ConfirmationModalContext';
import DropDownV2 from '../../UI/DropDownV2/DropDownV2';
import { getDropDownList } from '../../../axios/requests/dropDownReq';
import Flatpickr from 'react-flatpickr';
import heLocale from 'flatpickr/dist/l10n/he';
import 'flatpickr/dist/themes/material_green.css';

const ActivityModal = ({ showActivityModal, handleCloseModal, newActivity, handleInputChange, saveNewActivity, isEditMode, onDeleteActivity }) => {
    const [t] = useTranslation('common');
    const { showModal } = useContext(ConfirmationModalContext);
    const isMounted = useRef(true);

    const [activityNameOptions, setActivityNameOptions] = useState([]);
    const [selectedActivityName, setSelectedActivityName] = useState({ key: t('Select Activity Type'), value: t('Select Activity Type') });

    const [subjectOptions, setSubjectOptions] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState({ key: t('Select Subject Type'), value: t('Select Subject Type') });

    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedDate, setSelectedDate] = useState(newActivity.date);

    const toggleCalendar = () => {
        setShowCalendar(!showCalendar);
    };

    const dateSelectedHandler = (date) => {
        if (date[0]) {
            setSelectedDate(date[0]);
            handleInputChange({ target: { name: 'date', value: date[0] } });
        }
    };

    const requestDeleteConfirmation = () => {
        showModal(() => onDeleteActivity(newActivity._id), {
            titleKey: t('Deletion Header Modal'),
            bodyKey: t('Are you sure you want to delete this activity?'),
        });
    };

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);


    useEffect(() => {
        if (newActivity) {
            setSelectedActivityName({ key: newActivity.activityName, value: newActivity.activityName });
            setSelectedSubject({ key: newActivity.subject, value: newActivity.subject });

            if (newActivity?.activityName) {
                const fetchInitialSubjects = async () => {
                    try {
                        const subjectDropDownList = await getDropDownList('CONTACT_ACTIVITY_SUBJECT', newActivity?.activityName);
                        if (isMounted.current) {
                            const noneOption = { key: t('Select Subject Type'), value: t('Select Subject Type') };
                            const options = subjectDropDownList.length > 0 ? [noneOption, ...subjectDropDownList[0].dropDownItems] : [noneOption];
                            setSubjectOptions(options);
                            const currentSubject = options.find(option => option.key === newActivity.subject);
                            setSelectedSubject(currentSubject || noneOption);
                        }
                    } catch (error) {
                        console.error('Failed to fetch subjects:', error);
                    }
                };

                fetchInitialSubjects();
            }
        }
    }, [newActivity]);



    // fetch and set subjects based on the selected activity
    const fetchSubjects = useCallback(async (activityName) => {
        try {
            const subjectDropDownList = await getDropDownList('CONTACT_ACTIVITY_SUBJECT', activityName);
            if (isMounted.current) {
                const noneOption = { key: t('Select Subject Type'), value: t('Select Subject Type') };
                const options = subjectDropDownList.length > 0 ? [noneOption, ...subjectDropDownList[0].dropDownItems] : [noneOption];
                setSubjectOptions(options);
                setSelectedSubject(noneOption);
            }
        } catch (error) {
            console.error('Failed to fetch subjects:', error);
        }
    }, []);

    useEffect(() => {
        const fetchContactActivityData = async () => {
            try {
                const activityDropDownList = await getDropDownList('CONTACT_ACTIVITY');
                if (isMounted.current) {
                    const noneOption = { key: t('Select Activity Type'), value: t('Select Activity Type') };
                    setActivityNameOptions(activityDropDownList.length > 0 ? [noneOption, ...activityDropDownList[0].dropDownItems] : [noneOption]);
                    setSelectedActivityName(noneOption);
                }
            } catch (error) {
                console.error('Failed to fetch activity names:', error);
            }
        }
        fetchContactActivityData();
    }, []);

    const handleSelectActivityName = (selectedItem) => {
        setSelectedActivityName(selectedItem);
        handleInputChange({ target: { name: 'activityName', value: selectedItem.value } });

        // Reset the selected subject to "NONE" when a new activity is selected
        const noneOption = { key: t('Select Activity Type'), value: t('Select Activity Type') };
        setSelectedSubject(noneOption); // Reset subject to default option

        fetchSubjects(selectedItem.key);
    };

    const handleSelectSubject = (selectedItem) => {
        setSelectedSubject(selectedItem);
        handleInputChange({ target: { name: 'subject', value: selectedItem.value } });

    };

    return (
        <Modal show={showActivityModal} onHide={handleCloseModal} className={`${styles.modalCustomSize} ${styles.rtlModal}`} size="lg" >
            <Modal.Header>
                <Modal.Title>{t('Insert New Activity')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBodyStyle}>
                <Form id="activityForm" onSubmit={saveNewActivity} noValidate>
                    <Row>
                        <Col md={5}>
                            <DropDownV2
                                nowSelected={selectedActivityName}
                                items={activityNameOptions}
                                onSelect={handleSelectActivityName}
                                label={t('Activity Name')}
                                defaultSelectedItemName={t('Select Activity Type')}
                            />
                        </Col>
                        <Col md={5}>
                            <DropDownV2
                                nowSelected={selectedSubject}
                                items={subjectOptions}
                                onSelect={handleSelectSubject}
                                label={t('Activity Subject')}
                                defaultSelectedItemName={t('Select Subject Type')}
                            />
                        </Col>

                        {/* <Col md={5}>
                            <Form.Group controlId="formDate">
                                <Form.Label>{t('Activity Date')}</Form.Label>
                                <Form.Control type="datetime-local" name="date" value={newActivity.date} onChange={handleInputChange} />
                            </Form.Group>
                        </Col> */}
                        <Col md={5}>
                            <Form.Group controlId="formDate">
                                <Form.Label className={`${styles.formLabelBlock} ${styles.formInputWidth}`}>{t('Activity Date')}</Form.Label>
                                <Dropdown show={showCalendar} onToggle={toggleCalendar} drop="down">
                                    <Dropdown.Toggle className={styles['custom-dropdown-toggle']}>
                                        {selectedDate ? new Date(selectedDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : t('Select Date')}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <div>
                                            <Flatpickr
                                                id="notification-time-picker"
                                                options={{
                                                    locale: heLocale.he,
                                                    time_24hr: true,
                                                    enableTime: true,
                                                    defaultDate: selectedDate,
                                                    minDate: 'today',
                                                    inline: true,
                                                    dateFormat: 'd/m/Y H:i',
                                                }}
                                                value={selectedDate}
                                                onChange={dateSelectedHandler}
                                            />
                                            <div className={styles['button-container']}>
                                                <Button variant="secondary" onClick={() => { toggleCalendar(); setSelectedDate(new Date());}}>
                                                    {t('cancel')}
                                                </Button>
                                                <Button onClick={toggleCalendar} variant="success">{t('confirm')}</Button>
                                            </div>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </Col>

                        <Col md={5}>
                            <Form.Group controlId="formReminder">
                                <Form.Label>{t('Activity Reminder')}</Form.Label>
                                {/* <Form.Control type="number" name="remaindTimeMin" min={0} max={10} value={newActivity.remaindTimeMin} onChange={handleInputChange} /> */}
                                <Form.Control as="select" name="remaindTimeMin" value={newActivity.remaindTimeMin} onChange={handleInputChange}>
                                    <option value="0">{t('noRemaind')}</option>
                                    <option value="1">{t('1 min')}</option>
                                    <option value="5">{t('5 min')}</option>
                                    <option value="10">{t('10 min')}</option>
                                    <option value="15">{t('15 min')}</option>
                                    <option value="20">{t('20 min')}</option>
                                    <option value="30">{t('30 min')}</option>
                                    <option value="60">{t('1 hour')}</option>
                                    <option value="120">{t('2 hours')}</option>
                                    <option value="180">{t('3 hours')}</option>
                                    <option value="240">{t('4 hours')}</option>
                                    <option value="300">{t('5 hours')}</option>
                                    <option value="360">{t('6 hours')}</option>
                                    <option value="420">{t('7 hours')}</option>
                                    <option value="480">{t('8 hours')}</option>
                                    <option value="540">{t('9 hours')}</option>
                                    <option value="600">{t('10 hours')}</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={10}>
                            <Form.Group controlId="formRemarks">
                                <Form.Label>{t('Activity Remarks')}</Form.Label>
                                <Form.Control className={styles.minHeightContainer} as="textarea" rows={4} name="remarks" value={newActivity.remarks} onChange={handleInputChange} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {isEditMode && <Button variant="danger" onClick={requestDeleteConfirmation}>{t('delete')}</Button>}
                <Button form="activityForm" type='submit' className='btn-success'>{t('save')}</Button>
                <Button className="mr-auto" variant="secondary" onClick={handleCloseModal}>{t('Close')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ActivityModal;